'use client';

import { useEffect, useMemo, useState } from 'react';
import { createSchema } from '@ametista-ai/editor/schema';
import { ProseMirror, ProseMirrorDoc, reactKeys } from '@handlewithcare/react-prosemirror';
import { history, redo, undo } from 'prosemirror-history';
import { inputRules, wrappingInputRule } from 'prosemirror-inputrules';
import { EditorState } from 'prosemirror-state';

import 'prosemirror-tables/style/tables.css';
import 'prosemirror-view/style/prosemirror.css';

import { ALL_MARKS, ALL_NODES } from '@ametista-ai/editor/constants';
import {
  getCommandsFromExtensions,
  getInputRulesFromExtensions,
  getPluginsFromExtensions,
  parseContentToDoc,
} from '@ametista-ai/editor/utils';
import { baseKeymap } from 'prosemirror-commands';
import { gapCursor } from 'prosemirror-gapcursor';
import { keymap } from 'prosemirror-keymap';
import { Stack } from '@mantine/core';
import { getNodeViewsFromExtensions } from './utils';
import classes from '@ametista-ai/editor/styles.module.css';

const editable = () => false;

export function RichEditorReadOnly({
  content,
  style,
  className,
}: {
  content: string | null;
  style?: React.CSSProperties;
  className?: string;
}) {
  const extensions = useMemo(() => [...ALL_NODES, ...ALL_MARKS], []);

  const nodeViews = useMemo(() => getNodeViewsFromExtensions(extensions), [extensions]);

  const initialState = useMemo(() => {
    const schema = createSchema(extensions);
    const plugins = getPluginsFromExtensions(extensions, schema);
    const extensionsInputRules = getInputRulesFromExtensions(extensions, schema);
    const extensionsCommands = getCommandsFromExtensions(extensions, schema);

    return EditorState.create({
      schema,
      doc: content ? parseContentToDoc(content, schema) : undefined,
      plugins: [
        ...plugins,
        keymap({
          ...baseKeymap,
          ...extensionsCommands,
          'Mod-z': undo,
          'Mod-Shift-z': redo,
          'Mod-y': redo,
        }),
        gapCursor(),
        inputRules({
          rules: [wrappingInputRule(/^\s*([-+*])\s$/, schema.nodes.list), ...extensionsInputRules],
        }),
        history(),
        reactKeys(),
      ],
    });
  }, [content, extensions]);

  const [editorState, setEditorState] = useState<EditorState | null>(null);

  useEffect(() => {
    setEditorState(initialState);
  }, [initialState]);

  if (!editorState) {
    return null;
  }

  return (
    <ProseMirror
      state={editorState}
      nodeViews={nodeViews}
      dispatchTransaction={(tr) => {
        setEditorState((s) => s!.apply(tr));
      }}
      editable={editable}
    >
      <Stack gap="xs" h="100%" className={`${classes.editor} ${className}`} style={style}>
        <ProseMirrorDoc />
      </Stack>
    </ProseMirror>
  );
}
